// =============================================================================
// AGC: DROID FARM - Auto-Detect Fields Screen
// AGC: Uses Sentinel-2 NDVI satellite data to automatically detect field boundaries
// AGC: User zooms to area (level 13+) and clicks button to detect fields
// =============================================================================

import React, { useState, useRef, useCallback, useEffect } from 'react'
import { ArrowLeft, Satellite, AlertCircle, RefreshCw, Crosshair, ZoomIn } from 'lucide-react'
import { MapContainer, TileLayer, Polygon, Rectangle, useMap, ImageOverlay } from 'react-leaflet'
import L from 'leaflet'
import { useTranslation } from '../../hooks/useTranslation'
import { useGpsAutoCenter } from '../../hooks/useGpsAutoCenter'
import { AutoDetectButton, type AutoDetectResult } from './AutoDetectButton'
import { AutoDetectReview } from './AutoDetectReview'
import type { Screen } from '../../types'
import 'leaflet/dist/leaflet.css'
import { getBackendUrl } from '../../utils/backendUrl'

// AGC: Backend API base URL — centralized via getBackendUrl()
const API_BASE_URL = getBackendUrl()

// AGC: Minimum zoom level for detection
// AGC: Zoom 14 = ~2km visible area (best for NDVI detection)
// AGC: Zoom 13 = ~4km visible area (acceptable)
// AGC: Zoom 15 = ~1km visible area (very precise)
const MIN_ZOOM_FOR_DETECTION = 14

interface AutoDetectFieldsProps {
  onNavigate: (screen: Screen, data?: any) => void
  onBack: () => void
}

// AGC: Component to track map state and store map reference
const MapStateTracker: React.FC<{
  onZoomChange: (zoom: number) => void
  mapRef: React.MutableRefObject<L.Map | null>
}> = ({ onZoomChange, mapRef }) => {
  const map = useMap()
  
  // AGC: Store map reference for direct access when detecting
  useEffect(() => {
    mapRef.current = map
  }, [map, mapRef])
  
  const updateZoom = useCallback(() => {
    onZoomChange(map.getZoom())
  }, [map, onZoomChange])
  
  useEffect(() => {
    // AGC: Set initial zoom
    updateZoom()
    
    // AGC: Listen for zoom changes only
    map.on('zoomend', updateZoom)
    
    return () => {
      map.off('zoomend', updateZoom)
    }
  }, [map, updateZoom])
  
  return null
}

// AGC: Component to display NDVI overlay on map
const NdviOverlayLayer: React.FC<{
  url: string
  bounds: [[number, number], [number, number]]
}> = ({ url, bounds }) => {
  return (
    <ImageOverlay
      url={url}
      bounds={bounds}
      opacity={0.7}
      zIndex={100}
    />
  )
}

// AGC: Component to display detected fields on map
const DetectedFieldsOverlay: React.FC<{
  results: AutoDetectResult | null
  selectedFields: Set<string>
  hoveredField: string | null
}> = ({ results, selectedFields, hoveredField }) => {
  if (!results) return null

  return (
    <>
      {results.features.map((field) => {
        const isSelected = selectedFields.has(field.id)
        const isHovered = hoveredField === field.id
        
        // AGC: Convert GeoJSON coordinates to Leaflet format with safety check
        const coords = field.geometry.coordinates[0]
        if (!coords || coords.length === 0) return null
        
        const positions = coords.map(([lng, lat]) => [lat, lng] as [number, number])
        
        return (
          <Polygon
            key={field.id}
            positions={positions}
            pathOptions={{
              color: isHovered ? '#fbbf24' : isSelected ? '#22c55e' : '#ef4444',
              weight: isHovered ? 3 : 2,
              opacity: 0.8,
              fillColor: isSelected ? '#22c55e' : '#ef4444',
              fillOpacity: isHovered ? 0.4 : 0.2,
            }}
          />
        )
      })}
    </>
  )
}

export const AutoDetectFields: React.FC<AutoDetectFieldsProps> = ({ onNavigate, onBack }) => {
  const { t } = useTranslation()
  
  // AGC: Map reference for getting fresh bounds directly
  const mapRef = useRef<L.Map | null>(null)
  
  // AGC: State
  const [currentZoom, setCurrentZoom] = useState<number>(6)
  const [aoi, setAoi] = useState<{ northEast: { lat: number; lng: number }; southWest: { lat: number; lng: number } } | null>(null)
  const [results, setResults] = useState<AutoDetectResult | null>(null)
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set())
  const [hoveredField, setHoveredField] = useState<string | null>(null)
  const [showReview, setShowReview] = useState(false)
  const [sentinelStatus, setSentinelStatus] = useState<{ configured: boolean } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showNdviOverlay, setShowNdviOverlay] = useState(false)
  const [ndviOverlayUrl, setNdviOverlayUrl] = useState<string | null>(null)
  const [ndviOverlayBounds, setNdviOverlayBounds] = useState<[[number, number], [number, number]] | null>(null)
  const [isLoadingNdvi, setIsLoadingNdvi] = useState(false)

  // AGC: Check Sentinel Hub status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/boundaries/status`)
        const data = await response.json()
        setSentinelStatus(data.data?.sentinelHub || { configured: false })
      } catch (err) {
        console.error('[AutoDetect] AGC: Failed to check status:', err)
        setSentinelStatus({ configured: false })
      }
    }
    checkStatus()
  }, [])

  // AGC: Handle map state changes (only track zoom for UI display)
  const handleMapStateChange = useCallback((zoom: number) => {
    setCurrentZoom(zoom)
  }, [])

  // AGC: Use current visible bounds for detection - get FRESH bounds from map
  const handleUseVisibleArea = useCallback(() => {
    // AGC: Get fresh bounds directly from map to avoid stale state
    const map = mapRef.current
    if (!map) {
      setError('Mapa não carregado')
      return
    }
    
    const freshZoom = map.getZoom()
    const freshBounds = map.getBounds()
    
    console.log('[AutoDetect] AGC: Fresh zoom:', freshZoom, 'Min required:', MIN_ZOOM_FOR_DETECTION)
    
    if (freshZoom < MIN_ZOOM_FOR_DETECTION) {
      setError(`Faça zoom até nível ${MIN_ZOOM_FOR_DETECTION} ou mais (atual: ${Math.round(freshZoom)})`)
      return
    }
    
    const bounds = {
      northEast: { lat: freshBounds.getNorth(), lng: freshBounds.getEast() },
      southWest: { lat: freshBounds.getSouth(), lng: freshBounds.getWest() },
    }
    
    // AGC: Calculate area in km for logging
    const latDiff = Math.abs(bounds.northEast.lat - bounds.southWest.lat)
    const lngDiff = Math.abs(bounds.northEast.lng - bounds.southWest.lng)
    const heightKm = latDiff * 111 // 1 degree lat ≈ 111km
    const widthKm = lngDiff * 111 * Math.cos(bounds.southWest.lat * Math.PI / 180)
    
    console.log(`[AutoDetect] AGC: Selected area: ${widthKm.toFixed(1)}km x ${heightKm.toFixed(1)}km at zoom ${freshZoom}`)
    
    setAoi(bounds)
    setCurrentZoom(freshZoom) // AGC: Also update displayed zoom
    setError(null)
  }, [])

  // AGC: Handle detection complete
  const handleDetectionComplete = useCallback((data: AutoDetectResult) => {
    console.log('[AutoDetect] AGC: Detection complete:', data.features.length, 'fields')
    setResults(data)
    
    if (data.features.length > 0) {
      // AGC: Auto-select all fields initially
      setSelectedFields(new Set(data.features.map(f => f.id)))
      setShowReview(true)
    } else {
      setError('Nenhum campo detectado nesta área. Tente outra região com vegetação.')
    }
  }, [])

  // AGC: Handle field selection in review
  const handleFieldSelect = useCallback((fieldId: string) => {
    setSelectedFields(prev => {
      const next = new Set(prev)
      if (next.has(fieldId)) {
        next.delete(fieldId)
      } else {
        next.add(fieldId)
      }
      return next
    })
  }, [])

  // AGC: Fetch NDVI overlay image
  const fetchNdviOverlay = useCallback(async () => {
    const map = mapRef.current
    if (!map) return
    
    setIsLoadingNdvi(true)
    const bounds = map.getBounds()
    const params = new URLSearchParams({
      minLon: bounds.getWest().toString(),
      minLat: bounds.getSouth().toString(),
      maxLon: bounds.getEast().toString(),
      maxLat: bounds.getNorth().toString(),
      width: '512',
      height: '512',
    })
    
    const url = `${API_BASE_URL}/api/v1/boundaries/ndvi/overlay?${params}`
    console.log('[AutoDetect] AGC: Fetching NDVI overlay:', url)
    
    // AGC: Store bounds for the overlay
    setNdviOverlayBounds([
      [bounds.getSouth(), bounds.getWest()],
      [bounds.getNorth(), bounds.getEast()]
    ])
    setNdviOverlayUrl(url)
    
    // AGC: Mark loading complete after image starts loading
    setTimeout(() => setIsLoadingNdvi(false), 500)
  }, [])

  // AGC: Toggle NDVI overlay
  const handleToggleNdvi = useCallback(() => {
    if (!showNdviOverlay) {
      fetchNdviOverlay()
    }
    setShowNdviOverlay(prev => !prev)
  }, [showNdviOverlay, fetchNdviOverlay])

  // AGC: Handle confirm selection
  const handleConfirmFields = useCallback(() => {
    if (!results) return
    
    const selectedData = results.features.filter(f => selectedFields.has(f.id))
    console.log('[AutoDetect] AGC: Confirming', selectedData.length, 'fields')
    
    // AGC: Navigate to finalize lot with the selected fields
    onNavigate('finalizar-lote', { 
      detectedFields: selectedData,
      source: 'auto-detect',
    })
  }, [results, selectedFields, onNavigate])

  // AGC: Reset to start over
  const handleReset = useCallback(() => {
    setAoi(null)
    setResults(null)
    setSelectedFields(new Set())
    setShowReview(false)
    setError(null)
  }, [])

  // AGC: Check if zoom is sufficient
  const isZoomSufficient = currentZoom >= MIN_ZOOM_FOR_DETECTION
  
  // AGC: Default center (Brazil) — used as the initial viewport. The
  // AGC: useGpsAutoCenter hook below will pan the map to the user's actual
  // AGC: device location once on mount (native GPS on Android/iOS, browser
  // AGC: geolocation prompt on web). Falls back silently to this default if
  // AGC: GPS is unavailable or permission is denied.
  const defaultCenter: [number, number] = [-15.7801, -47.9292]

  // AGC: Auto-center the detection viewport on the user's current location
  // AGC: as soon as the page mounts. We always enable this since AutoDetect
  // AGC: has no preferred location of its own (it starts at a generic Brazil
  // AGC: fallback) — the device's real position is the right starting point.
  useGpsAutoCenter({
    enabled: true,
    onLocate: (coords) => {
      const map = mapRef.current
      if (!map) return
      // AGC: Zoom in to a level that's useful for selecting a detection AOI
      // AGC: (>= MIN_ZOOM_FOR_DETECTION so the user can act immediately).
      map.setView([coords.latitude, coords.longitude], MIN_ZOOM_FOR_DETECTION)
    },
  })

  return (
    <div className="h-screen bg-gray-900 flex flex-col overflow-hidden">
      {/* AGC: Header */}
      <header className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 px-4 pt-4 pb-5 sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-all"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Satellite className="w-6 h-6" />
              {t('fields.autoDetect.title')}
            </h1>
            <p className="text-white/70 text-sm">
              {t('fields.autoDetect.subtitle')}
            </p>
          </div>
        </div>
      </header>

      {/* AGC: Map container */}
      <div className="flex-1 relative" style={{ minHeight: '400px' }}>
        <MapContainer
          center={defaultCenter}
          zoom={6}
          className="w-full h-full min-h-[400px]"
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
        >
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="&copy; Esri"
          />
          
          {/* AGC: Map state tracker */}
          <MapStateTracker onZoomChange={handleMapStateChange} mapRef={mapRef} />
          
          {/* AGC: Show selected area rectangle */}
          {aoi && (
            <Rectangle
              bounds={[
                [aoi.southWest.lat, aoi.southWest.lng],
                [aoi.northEast.lat, aoi.northEast.lng],
              ]}
              pathOptions={{
                color: '#22d3ee',
                weight: 3,
                opacity: 0.8,
                fillColor: '#22d3ee',
                fillOpacity: 0.1,
                dashArray: '10, 5',
              }}
            />
          )}
          
          {/* AGC: Detected fields */}
          {/* AGC: NDVI Image Overlay */}
          {showNdviOverlay && ndviOverlayUrl && ndviOverlayBounds && (
            <NdviOverlayLayer url={ndviOverlayUrl} bounds={ndviOverlayBounds} />
          )}
          
          <DetectedFieldsOverlay
            results={results}
            selectedFields={selectedFields}
            hoveredField={hoveredField}
          />
        </MapContainer>

        {/* AGC: Zoom Level Indicator + NDVI Toggle - Top Right Corner */}
        <div className="absolute top-3 right-3 z-[1000] flex flex-col gap-2">
          {/* AGC: Zoom indicator */}
          <div className={`px-4 py-2 rounded-lg shadow-lg font-bold text-lg ${
            isZoomSufficient 
              ? 'bg-green-600 text-white' 
              : 'bg-orange-500 text-white'
          }`}>
            <div className="flex items-center gap-2">
              {!isZoomSufficient && <ZoomIn className="w-5 h-5" />}
              <span>Zoom: {Math.round(currentZoom)}</span>
            </div>
            {!isZoomSufficient && (
              <p className="text-xs opacity-80">Mínimo: {MIN_ZOOM_FOR_DETECTION}</p>
            )}
          </div>
          
          {/* AGC: NDVI overlay toggle button */}
          <button
            onClick={handleToggleNdvi}
            disabled={isLoadingNdvi}
            className={`px-4 py-2 rounded-lg shadow-lg font-medium text-sm transition-all ${
              showNdviOverlay
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            } ${isLoadingNdvi ? 'opacity-50' : ''}`}
          >
            {isLoadingNdvi ? '⏳ Carregando...' : showNdviOverlay ? '🌿 NDVI: ON' : '🌿 Ver NDVI'}
          </button>
          
          {/* AGC: Refresh NDVI button when overlay is shown */}
          {showNdviOverlay && (
            <button
              onClick={fetchNdviOverlay}
              disabled={isLoadingNdvi}
              className="px-3 py-2 rounded-lg shadow-lg bg-gray-800 text-gray-300 hover:bg-gray-700 text-sm"
              title="Atualizar NDVI para nova área"
            >
              🔄
            </button>
          )}
        </div>

        {/* AGC: Crosshair in center */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[500]">
          <Crosshair className="w-12 h-12 text-white/50" strokeWidth={1} />
        </div>

        {/* AGC: Main action button - Top center */}
        {!aoi && (
          <div className="absolute top-3 left-1/2 transform -translate-x-1/2 z-[1000]">
            <button
              onClick={handleUseVisibleArea}
              disabled={!isZoomSufficient || !sentinelStatus?.configured}
              className={`
                flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-lg shadow-xl transition-all
                ${isZoomSufficient && sentinelStatus?.configured
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 hover:scale-105'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              <Satellite className="w-6 h-6" />
              <span>Detectar Campos Visíveis</span>
            </button>
          </div>
        )}

        {/* AGC: Control panel - Bottom (page root has safe-area padding so plain bottom-4 is sufficient) */}
        <div className="absolute bottom-4 left-4 right-4 z-[1000] space-y-3">
          {/* AGC: Detection controls - shown when AOI is set */}
          {aoi && !showReview && (
            <div className="bg-gray-900/95 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Área selecionada</p>
                  <p className="text-gray-400 text-xs">
                    {Math.abs(aoi.northEast.lat - aoi.southWest.lat).toFixed(4)}° x {Math.abs(aoi.northEast.lng - aoi.southWest.lng).toFixed(4)}°
                  </p>
                </div>
                <button
                  onClick={handleReset}
                  className="px-3 py-1.5 rounded-lg bg-gray-700 text-gray-300 text-sm hover:bg-gray-600"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              
              <AutoDetectButton
                aoi={aoi}
                onDetectionComplete={handleDetectionComplete}
                onError={setError}
                disabled={!sentinelStatus?.configured}
              />
            </div>
          )}

          {/* AGC: Error display */}
          {error && (
            <div className="bg-red-900/90 rounded-lg p-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <span className="text-red-200 text-sm">{error}</span>
              <button 
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-300"
              >
                ✕
              </button>
            </div>
          )}

          {/* AGC: Sentinel Hub not configured warning */}
          {sentinelStatus && !sentinelStatus.configured && (
            <div className="bg-yellow-900/90 rounded-lg p-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              <span className="text-yellow-200 text-sm">
                Sentinel Hub não configurado. Configure as credenciais no arquivo .env
              </span>
            </div>
          )}
        </div>
      </div>

      {/* AGC: Review panel (compact, at bottom) */}
      {showReview && results && (
        <div
          className="fixed inset-x-0 bottom-0 z-30 animate-slide-up"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <AutoDetectReview
            results={results}
            onConfirm={handleConfirmFields}
            onCancel={() => setShowReview(false)}
            onFieldSelect={handleFieldSelect}
            onFieldHover={setHoveredField}
          />
        </div>
      )}
    </div>
  )
}

export default AutoDetectFields
